import { useMemo, type ReactElement } from 'react'
import { parseAsString, useQueryState } from 'nuqs'
import { parseAsTerrosId } from '@util'
import {
  isDefined,
  isEntityId,
  isNotDefined,
  isNotEmpty,
  isRoleId,
  type CompanyId,
  type DashboardDataWithEditable,
  type DashboardScopeId,
  type EntityId,
} from '@terros/common'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Stack, Text } from '@mantine/core'
import {
  getHustleDashboards,
  SizePrimitives,
  toDashboardAudience,
  useDashboards,
  useProfile,
  useTranslation,
} from '@front/model'
import { HeroImage, MaterialSymbolIcon, NotReady, Page } from '@components'
import { useDashboardList } from './useDashboardList'
import { useDashboardCreate } from './useDashboardCreate'
import type { DashboardScopeTarget } from './types'
import { HustleSection, useHustleActions } from './hustle'
import { getDashboardScopeTarget } from './dashboardScope'
import { DashboardListToolbar } from './DashboardListToolbar'
import { DashboardGroupSection } from './DashboardGroupSection'
import { DashboardAudiencePickerModal } from './audiencePicker'

const isDashboardScopeId = (value: string | DashboardScopeId): value is DashboardScopeId =>
  isEntityId(value) || isRoleId(value)

export function DashboardList(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { company, user, isLoading: isProfileLoading, hasPermission } = useProfile()
  const canListDashboards = !isProfileLoading && hasPermission('dashboard:list')
  const [scopeId, setScopeId] = useQueryState('scopeId', parseAsTerrosId(isDashboardScopeId))
  const [scopeName, setScopeName] = useQueryState('scopeName', parseAsString)
  const selectedTarget: DashboardScopeTarget | undefined = useMemo(() => {
    return getDashboardScopeTarget({ scopeId, scopeName, companyName: company?.name, companyLabel: t('Company') })
  }, [company?.name, scopeId, scopeName, t])
  const listInput = useMemo(() => {
    if (isNotDefined(selectedTarget)) return undefined
    return { filter: { entityId: selectedTarget.entityId } }
  }, [selectedTarget])
  const {
    data: dashboards,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useDashboards({
    ...listInput,
    enabled: canListDashboards,
  })
  const dashboardList = useDashboardList({
    listInput,
    scopedDashboards: dashboards ?? [],
    viewerUserId: user?.userId,
  })

  const scopeEntityId = selectedTarget?.entityId ?? user?.userId
  const hustle = useMemo(
    () =>
      getHustleDashboards({
        dashboards: dashboards ?? [],
        scopeEntityId,
        viewerUserId: user?.userId,
      }),
    [dashboards, scopeEntityId, user?.userId]
  )
  const hustleActions = useHustleActions({
    hustle,
    listInput,
    scopeEntityId: selectedTarget?.entityId,
    viewerUserId: user?.userId,
  })
  const create = useDashboardCreate({ listInput, user })

  const initialAudience = useMemo(
    () => toDashboardAudience({ scopeId: scopeEntityId, scopeName: selectedTarget?.name, viewerUserId: user?.userId }),
    [scopeEntityId, selectedTarget?.name, user?.userId]
  )

  if (isProfileLoading) return <NotReady isLoading />
  if (!canListDashboards) return <NotReady error={t('You do not have permission to view dashboards')} />

  if (isLoading || error || !dashboards)
    return <NotReady data={dashboards} error={error} id='Dashboards' isLoading={isLoading} />

  const handleSelectTarget = (target?: DashboardScopeTarget): void => {
    setScopeId(target?.entityId ?? null)
    setScopeName(target?.name ?? null)
    dashboardList.resetInteractionState()
  }

  const openDashboard = (dashboard: DashboardDataWithEditable): void => {
    navigate({ to: `/dashboard/${dashboard.dashboardId}/edit` })
  }

  const handleCreateHustle = async (): Promise<void> => {
    const created = await hustleActions.createHustle()
    if (isNotDefined(created)) return
    await navigate({ to: `/dashboard/${created.dashboardId}/edit` })
  }

  const normalDashboards = dashboardList.displayDashboardsByGroup.normal

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title='Dashboards'>
        <DashboardListToolbar
          canReorder={isNotEmpty(dashboardList.orderedDashboards) && dashboardList.hasEditableDashboard}
          isCreating={create.isSubmitting}
          isReordering={dashboardList.isReordering}
          permissionTargetId={toPermissionTargetId({ scopeEntityId, companyId: company?.companyId })}
          selectedTarget={selectedTarget}
          startReordering={dashboardList.startReordering}
          onNewDashboard={create.openCreate}
          onSelectTarget={handleSelectTarget}
          onStopReordering={dashboardList.stopReordering}
        />
      </Page.Heading>
      <Page.Content scrollArea>
        <Stack gap='xl'>
          <Text c='dimmed' size='sm'>
            {selectedTarget?.name ?? t('Yours, and any shared with you')}
          </Text>

          <HustleSection
            copyingDashboardId={hustleActions.copyingDashboardId}
            deletion={{
              deletingDashboardId: dashboardList.deletingDashboardId,
              isRemoving: dashboardList.isRemoving,
              onDelete: dashboardList.handleDelete,
            }}
            hustle={hustle}
            isCreating={hustleActions.isCreating}
            onCopy={hustleActions.copyHustle}
            onCreate={handleCreateHustle}
            onDuplicate={create.openDuplicate}
            onOpen={openDashboard}
          />

          {isNotEmpty(normalDashboards) ? (
            <DashboardGroupSection
              dashboards={normalDashboards}
              deletion={{
                deletingDashboardId: dashboardList.deletingDashboardId,
                isRemoving: dashboardList.isRemoving,
                onDelete: dashboardList.handleDelete,
              }}
              group='normal'
              reorder={{
                draggingDashboardId: dashboardList.draggingDashboardId,
                isReordering: dashboardList.isReordering,
                onDragEnd: dashboardList.handleDragEnd,
                onDragOver: dashboardList.handleDragOver,
                onDragStart: dashboardList.handleDragStart,
                onDrop: dashboardList.handleDrop,
              }}
              scopeEntityId={scopeEntityId}
              title={t('Dashboards')}
              onDuplicate={create.openDuplicate}
            />
          ) : (
            <HeroImage>
              <MaterialSymbolIcon icon='dashboard' size={SizePrimitives['12']} />
              <Text c='dimmed' size='lg'>
                {t('No Dashboards')}
              </Text>
            </HeroImage>
          )}
        </Stack>
      </Page.Content>

      <DashboardAudiencePickerModal
        companyId={company?.companyId}
        companyName={company?.name}
        initialAudience={initialAudience}
        isSubmitting={create.isSubmitting}
        opened={create.isPickerOpen}
        title={isDefined(create.duplicateSource) ? 'Duplicate Dashboard' : 'New Dashboard'}
        user={user}
        onClose={create.closePicker}
        onContinue={create.submit}
      />
    </Page>
  )
}

/**
 * A role isn't a permission target in its own right, so a role-scoped view checks
 * `dashboard:manage` against the company instead.
 */
function toPermissionTargetId({
  scopeEntityId,
  companyId,
}: {
  scopeEntityId: DashboardScopeId | undefined
  companyId: CompanyId | undefined
}): EntityId | undefined {
  if (isNotDefined(scopeEntityId)) return undefined
  if (isRoleId(scopeEntityId)) return companyId
  return scopeEntityId
}

export const Route = createFileRoute('/dashboard/')({
  component: DashboardList,
})
